'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect } from 'react';

import { OPEN_DAY } from '@/config/openDay';
import { useOpenDay } from '@/hooks/useOpenDay';
import { capture } from '@/config/captureUserEvent';
import { EventNames } from '@/config/eventNames';

/**
 * Site-wide announcement bar for the open day.
 *
 * Deliberately a bar and not an interstitial: the site's primary conversion is
 * the free-trial booking, and a modal that blocks it would trade a membership
 * for a free RSVP. The bar announces without interrupting.
 *
 * Renders nothing once the event is over, on the open day page itself, or
 * after the visitor dismisses it.
 */
export function OpenDayBanner() {
  const pathname = usePathname();
  const { mounted, isOver, isLive, isImminent, dismissed, dismiss, countdown } =
    useOpenDay();

  const onOwnPage = pathname === OPEN_DAY.path;
  const visible = mounted && !isOver && !dismissed && !onOwnPage;

  useEffect(() => {
    if (visible) {
      capture(EventNames.openDayBannerViewed, { live: isLive });
    }
  }, [visible, isLive]);

  // Reserving the bar's height before mount would push the hero down on every
  // page load for a promo most visitors have already dismissed, so we render
  // nothing until we know.
  if (!visible) return null;

  return (
    <div className="relative isolate w-full bg-gray-900 text-white">
      <Link
        href={OPEN_DAY.path}
        onClick={() =>
          capture(EventNames.openDayBannerClicked, { live: isLive })
        }
        className="block px-4 py-2.5 pr-12 transition-colors hover:bg-white/5 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-white sm:px-6 lg:px-8"
      >
        <p className="mx-auto flex max-w-7xl flex-wrap items-baseline justify-center gap-x-3 gap-y-1 text-center text-sm leading-6">
          <span className="font-bold">
            {isLive
              ? 'Our Studio Open Day is on right now'
              : `Studio Open Day, ${OPEN_DAY.dayLabel}`}
          </span>
          <span className="text-gray-300">
            {isLive
              ? `Open until ${OPEN_DAY.timeLabel.split(' - ')[1]}, come and say hello`
              : `${OPEN_DAY.timeLabel} in ${OPEN_DAY.suburb}, free but limited spots`}
          </span>
          <span className="font-bold text-green-400 underline underline-offset-4">
            {isLive ? 'Get directions' : 'Details and free tickets'}
          </span>
          {isImminent && !isLive && (
            <span className="tabular-nums text-gray-400">
              {countdown.days > 0
                ? `in ${countdown.days} ${countdown.days === 1 ? 'day' : 'days'}`
                : `in ${countdown.hours}h ${countdown.minutes}m`}
            </span>
          )}
        </p>
      </Link>

      <button
        type="button"
        onClick={() => {
          capture(EventNames.openDayBannerDismissed);
          dismiss();
        }}
        aria-label="Dismiss open day announcement"
        className="absolute right-1 top-1/2 -translate-y-1/2 rounded-md p-2 text-gray-400 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-white sm:right-2"
      >
        <svg
          className="h-4 w-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.5}
          strokeLinecap="round"
          aria-hidden="true"
        >
          <path d="M6 18 18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}
